import { useEffect } from "react";

// The hook adds this class, so it owns the style — that keeps the highlight
// working anywhere the hook is used, including Storybook.
import "./fieldFlash.scss";

/**
 * Scrolls to the field the sidebar's search selected and flashes it, once.
 *
 * Anchors are `data-field` attributes stamped by the field controls (see
 * configFields.tsx). Lookup is by attribute rather than by ref because the field
 * lives inside whichever section just rendered, several layers below the page.
 *
 * `activeSection` is a dependency so the effect re-runs after the target section
 * has mounted — selecting a search hit changes both at once, and the element does
 * not exist until the new section paints.
 *
 * A field with no anchor, or a section that is itself the field, simply opens
 * without a scroll; `onDone` still fires so the request isn't left pending.
 */
export function useFieldFlash(
  pendingField: string | null,
  activeSection: string,
  onDone: () => void,
) {
  useEffect(() => {
    if (!pendingField) return;
    onDone();
    const el = document.querySelector<HTMLElement>(
      `[data-field="${CSS.escape(pendingField)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("field-flash");
    const timer = window.setTimeout(
      () => el.classList.remove("field-flash"),
      1600,
    );
    return () => window.clearTimeout(timer);
    // onDone is a setState call; including it would re-run the effect on every
    // render of the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingField, activeSection]);
}
